import React, { memo } from 'react';

import orderBy from 'lodash.orderby';
import type { FieldValues } from 'react-hook-form';

import type { QuickAddSectionProps, QuickItemRenderer } from '@/_containers';
import { AsyncQuickAddSection, phoneNumberIncludeKey } from '@/_containers';
import type { TeamMemberQueryLinkerSchema } from '@/_schemas/team-members/team-members-query-linker-schema';
import { nameResolver } from '@/_shared';

import { Item } from './item';

import { useGetTeamMembersMutation } from '../../apis';

type TeamMemberMultiSelectProps<TFormValues extends FieldValues = FieldValues> = Pick<
  QuickAddSectionProps<TeamMemberQueryLinkerSchema, TFormValues>,
  'isEditable' | 'control' | 'fieldName' | 'title' | 'sectionTitle' | 'icon' | 'rightSlot' | 'paginatedItems'
> & {
  teamMemberId?: string | number;
  items: TeamMemberQueryLinkerSchema[];
  showHeader?: boolean;
  emptyDataMessage?: string;
};

const apiParams = { include: phoneNumberIncludeKey() };

function SyncTeamMemberMultiSelectBase<TFormValues extends FieldValues = FieldValues>({
  teamMemberId,
  title = 'Team Members',
  sectionTitle = 'Team Members',
  items = [],
  emptyDataMessage = 'No Team Members',
  ...rest
}: TeamMemberMultiSelectProps<TFormValues>) {
  const renderItem: QuickItemRenderer<TeamMemberQueryLinkerSchema> = React.useCallback(
    (item, { isEditable, removeItem }) => (
      <Item key={item.id} item={item} isEditable={isEditable} removeItemHandler={removeItem} />
    ),
    []
  );

  const itemToString = React.useCallback(
    (item: TeamMemberQueryLinkerSchema | null) => (item ? nameResolver(item.first_name, item.last_name) : ''),
    []
  );

  const keyExtractor = React.useCallback((item: TeamMemberQueryLinkerSchema) => item.id.toString(), []);

  const sortValues = React.useCallback(
    (values: TeamMemberQueryLinkerSchema[]) => orderBy(values, itemToString),
    [itemToString]
  );

  const filterOptions = React.useCallback(
    (item: TeamMemberQueryLinkerSchema) => (teamMemberId ? item.id === Number(teamMemberId) : false),
    [teamMemberId]
  );

  const sortedItems = React.useMemo(() => sortValues(items), [items, sortValues]);

  return (
    <AsyncQuickAddSection
      {...rest}
      sectionTitle={sectionTitle}
      title={title}
      emptyDataMessage={emptyDataMessage}
      items={sortedItems}
      keyExtractor={keyExtractor}
      itemToString={itemToString}
      renderItem={renderItem}
      useApi={useGetTeamMembersMutation}
      sortValues={sortValues}
      filterOptions={filterOptions}
      apiParams={apiParams}
    />
  );
}

export const SyncTeamMemberMultiSelect = memo(SyncTeamMemberMultiSelectBase) as typeof SyncTeamMemberMultiSelectBase;
